import { isEqual, uniqBy } from 'lodash';
import type { ReactNode } from 'react';

export type MakeModelOption = {
  value: string;
  label?: ReactNode;
};

/** Selected model under a Make (`model` may be '' for None). */
export type SelectedMakeModel = {
  make: string;
  model: string;
};

/** Nested picker state — models are make-scoped to avoid shared-name collisions. */
export type MakeModelSelection = {
  makes: string[];
  models: SelectedMakeModel[];
};

export type MakeCheckState = 'checked' | 'indeterminate' | 'unchecked';

export const isModelSelected = (
  makeValue: string,
  modelValue: string,
  selection: MakeModelSelection
): boolean => selection.models.some((m) => m.make === makeValue && m.model === modelValue);

/** Pass the full catalog when modelsByMake is a search subset. None counts only when that catalog has no real Models. */
export const getToggleableModels = (
  makeValue: string,
  modelsByMake: Record<string, MakeModelOption[]>,
  allModelsByMake: Record<string, MakeModelOption[]> = modelsByMake
): string[] => {
  const catalog = allModelsByMake[makeValue] ?? modelsByMake[makeValue] ?? [];
  const hasRealModels = catalog.some((model) => model.value !== '');
  return (modelsByMake[makeValue] ?? [])
    .map((model) => model.value)
    .filter((value) => !hasRealModels || value !== '');
};

export const getMakeCheckState = (
  makeValue: string,
  modelsByMake: Record<string, MakeModelOption[]>,
  selection: MakeModelSelection,
  allModelsByMake: Record<string, MakeModelOption[]> = modelsByMake
): MakeCheckState => {
  const makeModels = modelsByMake[makeValue] ?? [];

  if (!makeModels.length) {
    return selection.makes.includes(makeValue) ? 'checked' : 'unchecked';
  }

  // None counts toward the row highlight. An empty toggleable set is not fully checked.
  const anySelected = makeModels.some((model) =>
    isModelSelected(makeValue, model.value, selection)
  );
  if (!anySelected) {
    return 'unchecked';
  }

  const toggleable = getToggleableModels(makeValue, modelsByMake, allModelsByMake);
  if (
    toggleable.length > 0 &&
    toggleable.every((value) => isModelSelected(makeValue, value, selection))
  ) {
    return 'checked';
  }

  return 'indeterminate';
};

/** Fully-checked Make whose only model option is None — treat as make-only in payload. */
const isNoneOnlyFullyCheckedMake = (
  makeValue: string,
  modelsByMake: Record<string, MakeModelOption[]>,
  selection: MakeModelSelection
): boolean => {
  const makeModels = modelsByMake[makeValue] ?? [];
  return (
    getMakeCheckState(makeValue, modelsByMake, selection) === 'checked' &&
    makeModels.length > 0 &&
    makeModels.every((m) => m.value === '')
  );
};

/** Flatten nested selection to assetMake / assetModel (+ pairs for exact restore). */
export const toFilterPayload = (
  selection: MakeModelSelection,
  modelsByMake: Record<string, MakeModelOption[]>
): { makes: string[]; models: string[]; pairs: SelectedMakeModel[] } => {
  const concreteModels = new Set<string>();
  let includeNone = false;

  selection.models.forEach(({ make, model }) => {
    if (model === '') {
      if (!isNoneOnlyFullyCheckedMake(make, modelsByMake, selection)) {
        includeNone = true;
      }
      return;
    }
    concreteModels.add(model);
  });

  // Flat assetModel is what GraphQL receives; a None-only Make is otherwise dropped once another Make adds concrete models.
  if (
    concreteModels.size > 0 &&
    selection.makes.some((make) => isNoneOnlyFullyCheckedMake(make, modelsByMake, selection))
  ) {
    includeNone = true;
  }

  const models = [...concreteModels, ...(includeNone ? [''] : [])];

  // Align pairs with flat payload; omit None-only fully-checked Makes.
  const pairs = selection.models.filter(({ make, model }) => {
    if (model === '') {
      return !isNoneOnlyFullyCheckedMake(make, modelsByMake, selection);
    }
    return concreteModels.has(model);
  });

  return {
    makes: selection.makes,
    models,
    pairs,
  };
};

/** Client-only keys — strip in getFilterBy before GraphQL. */
export const ASSET_MAKE_MODEL_PAIRS_KEY = 'assetMakeModelPairs';
export const COMPONENT_MAKE_MODEL_PAIRS_KEY = 'componentMakeModelPairs';
export const MAKE_MODEL_CLIENT_ONLY_FILTER_KEYS = [
  ASSET_MAKE_MODEL_PAIRS_KEY,
  COMPONENT_MAKE_MODEL_PAIRS_KEY,
] as const;

export type MakeModelGraphqlKeyMap = {
  assetMakes: string;
  assetModels: string;
  componentMakes: string;
  componentModels: string;
};

/** GraphQL FilterBy keys for Make/Model. Pair keys are `MAKE_MODEL_CLIENT_ONLY_FILTER_KEYS`. */
export const DEFAULT_MAKE_MODEL_GRAPHQL_KEY_MAP: MakeModelGraphqlKeyMap = {
  assetMakes: 'assetMake',
  assetModels: 'assetModel',
  componentMakes: 'componentMake',
  componentModels: 'componentModel',
};

export const makeModelFilterKeys = (keyMap: MakeModelGraphqlKeyMap) =>
  new Set([
    keyMap.assetMakes,
    keyMap.assetModels,
    ASSET_MAKE_MODEL_PAIRS_KEY,
    keyMap.componentMakes,
    keyMap.componentModels,
    COMPONENT_MAKE_MODEL_PAIRS_KEY,
  ]);

/**
 * Drop Make/Model (+ pair) keys when Site changes. Asset/component IDs are pruned separately
 * by Site membership; these must be cleared entirely because options are Site-scoped.
 */
export const omitMakeModelFiltersForSiteChange = <T extends { key: string }>(
  filters: T[] | null | undefined,
  keyMap: MakeModelGraphqlKeyMap = DEFAULT_MAKE_MODEL_GRAPHQL_KEY_MAP
): T[] | null | undefined => {
  if (!filters?.length) {
    return filters;
  }
  const keysToClear = makeModelFilterKeys(keyMap);
  return filters.filter(({ key }) => !keysToClear.has(key));
};

/**
 * Tracks Site filter across effect runs.
 * Ref starts as `null` so the first run never clears dependents (allows restore).
 * Empty values (`undefined` / `null` / `[]`) are equivalent so table init cannot
 * spuriously clear Make/Model after Apply.
 */
export const consumeSiteFilterChange = (
  previousSiteFilterRef: { current: string[] | null | undefined },
  site: string[] | null | undefined
): boolean => {
  const normalize = (value: string[] | null | undefined) => (value?.length ? value : undefined);
  const previous = previousSiteFilterRef.current;
  const normalizedSite = normalize(site);
  if (previous === null) {
    previousSiteFilterRef.current = normalizedSite;
    return false;
  }
  previousSiteFilterRef.current = normalizedSite;
  return !isEqual(normalize(previous), normalizedSite);
};

const MAKE_MODEL_PAIR_SEPARATOR = '::';

/**
 * encodeURIComponent escapes `:` (and `/`, etc.), so values that contain the separator
 * cannot collide with the join — round-trip is safe without a custom escape scheme.
 * Do not change this separator: saved views already persist pairs in this form.
 */
export const encodeMakeModelPair = ({ make, model }: SelectedMakeModel): string =>
  `${encodeURIComponent(make)}${MAKE_MODEL_PAIR_SEPARATOR}${encodeURIComponent(model)}`;

export const decodeMakeModelPair = (value: string): SelectedMakeModel | null => {
  const separatorIndex = value.indexOf(MAKE_MODEL_PAIR_SEPARATOR);
  if (separatorIndex < 0) {
    return null;
  }
  try {
    return {
      make: decodeURIComponent(value.slice(0, separatorIndex)),
      model: decodeURIComponent(value.slice(separatorIndex + MAKE_MODEL_PAIR_SEPARATOR.length)),
    };
  } catch {
    return null;
  }
};

export const encodeMakeModelPairs = (pairs: SelectedMakeModel[]): string[] =>
  pairs.map(encodeMakeModelPair);

export const decodeMakeModelPairs = (values: string[] | null | undefined): SelectedMakeModel[] =>
  (values ?? [])
    .filter((value): value is string => typeof value === 'string')
    .map(decodeMakeModelPair)
    .filter((pair): pair is SelectedMakeModel => pair !== null);

/** Prefer pairs when present; else flat Make then Model (Cartesian). */
export const matchesMakeModelSelection = (
  makeValue: string | null | undefined,
  modelValue: string | null | undefined,
  selection: {
    makes?: string[] | null;
    models?: string[] | null;
    pairs?: SelectedMakeModel[] | null;
  }
): boolean => {
  const { makes, models, pairs } = selection;

  if (pairs?.length) {
    if (makeValue === undefined || makeValue === null) {
      return false;
    }

    // Makes omitted from pairs are make-only (e.g. None-only fully checked). Match any Model.
    const makeHasPairs = pairs.some((pair) => pair.make === makeValue);
    if (!makeHasPairs) {
      return Boolean(makes?.includes(makeValue));
    }

    if (modelValue === undefined || modelValue === null) {
      return false;
    }
    return pairs.some((pair) => pair.make === makeValue && pair.model === modelValue);
  }

  // Independent Make / Model checks (legacy). Empty Makes must not skip Models.
  if (makes?.length) {
    if (makeValue === undefined || makeValue === null || !makes.includes(makeValue)) {
      return false;
    }
  }

  if (models?.length) {
    if (modelValue === undefined || modelValue === null || !models.includes(modelValue)) {
      return false;
    }
  }

  return true;
};

/** '' is shared across Makes. Attach it only to a Make with no real Model selected. */
export const deriveNoneModelMakes = (
  selectedMakes: string[],
  selectedModels: string[],
  modelsByMake: Record<string, MakeModelOption[]>
): string[] => {
  if (!selectedModels.includes('')) {
    return [];
  }

  return selectedMakes.filter((makeValue) => {
    const makeModels = modelsByMake[makeValue] ?? [];
    if (!makeModels.some((m) => m.value === '')) {
      return false;
    }

    const concreteModels = makeModels.filter((m) => m.value !== '');
    const selectedConcreteCount = concreteModels.filter((m) =>
      selectedModels.includes(m.value)
    ).length;

    return selectedConcreteCount === 0;
  });
};

/** Expand make-only selections to toggleable models so None stays off when real Models exist. */
export const expandMakeOnlySelections = (
  selection: MakeModelSelection,
  modelsByMake: Record<string, MakeModelOption[]>
): MakeModelSelection => {
  const models = [...selection.models];

  selection.makes.forEach((makeValue) => {
    const makeModels = modelsByMake[makeValue] ?? [];
    if (!makeModels.length) {
      return;
    }

    const hasAnySelected = makeModels.some((m) => isModelSelected(makeValue, m.value, selection));
    if (hasAnySelected) {
      return;
    }

    getToggleableModels(makeValue, modelsByMake).forEach((model) => {
      models.push({ make: makeValue, model });
    });
  });

  return {
    makes: selection.makes,
    models,
  };
};

/** Build make-scoped pairs from flat Make/Model lists. */
export const pairsFromFilterPayload = (
  selectedMakes: string[],
  selectedModels: string[],
  modelsByMake: Record<string, MakeModelOption[]>
): SelectedMakeModel[] => {
  const models: SelectedMakeModel[] = [];

  selectedMakes.forEach((makeValue) => {
    const makeModelOptions = modelsByMake[makeValue] ?? [];
    selectedModels.forEach((modelValue) => {
      if (modelValue === '') {
        return;
      }
      if (makeModelOptions.some((m) => m.value === modelValue)) {
        models.push({ make: makeValue, model: modelValue });
      }
    });
  });

  deriveNoneModelMakes(selectedMakes, selectedModels, modelsByMake).forEach((makeValue) => {
    models.push({ make: makeValue, model: '' });
  });

  return models;
};

/** Merge prior None pairs when flat payload cannot express them. */
export const mergeModelPairsFromPayload = (
  previous: SelectedMakeModel[],
  selectedMakes: string[],
  selectedModels: string[],
  modelsByMake: Record<string, MakeModelOption[]>
): SelectedMakeModel[] => {
  const derived = pairsFromFilterPayload(selectedMakes, selectedModels, modelsByMake);

  if (!selectedModels.includes('')) {
    return derived.filter((m) => m.model !== '');
  }

  const preservedNones = previous.filter((p) => p.model === '' && selectedMakes.includes(p.make));
  if (!preservedNones.length) {
    return derived;
  }

  const preservedNoneMakes = new Set(preservedNones.map((p) => p.make));
  return [
    ...derived.filter((m) => m.model !== ''),
    ...preservedNones,
    ...derived.filter((m) => m.model === '' && !preservedNoneMakes.has(m.make)),
  ];
};

export const fromFilterPayload = (
  selectedMakes: string[],
  selectedModels: string[],
  modelsByMake: Record<string, MakeModelOption[]>
): MakeModelSelection =>
  expandMakeOnlySelections(
    {
      makes: selectedMakes,
      models: pairsFromFilterPayload(selectedMakes, selectedModels, modelsByMake),
    },
    modelsByMake
  );

export const getTagLabel = (model: string, make: string): string => `${model} / ${make}`;

export const getSelectedModelTags = (
  selection: MakeModelSelection,
  modelsByMake: Record<string, MakeModelOption[]>,
  makes: MakeModelOption[]
): { model: string; make: string; label: string }[] => {
  const makeLabelByValue = new Map(
    makes.map((m) => [m.value, typeof m.label === 'string' ? m.label : m.value])
  );

  const tags: { model: string; make: string; label: string }[] = [];
  const makesToScan = selection.makes.length ? selection.makes : Object.keys(modelsByMake);

  makesToScan.forEach((makeValue) => {
    const makeModels = modelsByMake[makeValue] ?? [];
    makeModels.forEach((modelOption) => {
      if (!isModelSelected(makeValue, modelOption.value, selection)) {
        return;
      }
      if (tags.some((t) => t.model === modelOption.value && t.make === makeValue)) {
        return;
      }

      const makeDisplay = makeLabelByValue.get(makeValue) ?? makeValue;
      const modelDisplay =
        typeof modelOption.label === 'string' ? modelOption.label : modelOption.value;

      tags.push({
        model: modelOption.value,
        make: makeValue,
        label: getTagLabel(modelDisplay, makeDisplay),
      });
    });
  });

  return tags;
};

export const toggleMakeSelection = (
  makeValue: string,
  modelsByMake: Record<string, MakeModelOption[]>,
  current: MakeModelSelection,
  allModelsByMake: Record<string, MakeModelOption[]> = modelsByMake
): MakeModelSelection => {
  const state = getMakeCheckState(makeValue, modelsByMake, current, allModelsByMake);
  const makeModelOptions = modelsByMake[makeValue] ?? [];
  const toggleable = getToggleableModels(makeValue, modelsByMake, allModelsByMake);

  if (state !== 'checked') {
    const toAdd = toggleable.filter((model) => !isModelSelected(makeValue, model, current));
    if (!toAdd.length && makeModelOptions.length) {
      return current;
    }

    return {
      makes: current.makes.includes(makeValue) ? current.makes : [...current.makes, makeValue],
      models: [...current.models, ...toAdd.map((model) => ({ make: makeValue, model }))],
    };
  }

  // Make uncheck clears visible models and None. Hidden real models under this Make stay.
  const toggleableSet = new Set(toggleable);
  const remaining = current.models.filter(
    (model) => model.make !== makeValue || (!toggleableSet.has(model.model) && model.model !== '')
  );
  const remainingForMake = remaining.some((model) => model.make === makeValue);
  return {
    makes: remainingForMake ? current.makes : current.makes.filter((make) => make !== makeValue),
    models: remaining,
  };
};

export const toggleModelSelection = (
  makeValue: string,
  modelValue: string,
  modelsByMake: Record<string, MakeModelOption[]>,
  current: MakeModelSelection
): MakeModelSelection => {
  const isSelected = isModelSelected(makeValue, modelValue, current);

  if (isSelected) {
    const nextModels = current.models.filter(
      (m) => !(m.make === makeValue && m.model === modelValue)
    );
    const remainingForMake = nextModels.some((m) => m.make === makeValue);
    return {
      makes: remainingForMake ? current.makes : current.makes.filter((m) => m !== makeValue),
      models: nextModels,
    };
  }

  return {
    makes: current.makes.includes(makeValue) ? current.makes : [...current.makes, makeValue],
    models: [...current.models, { make: makeValue, model: modelValue }],
  };
};

export const filterMakesAndModelsBySearch = (
  makes: MakeModelOption[],
  modelsByMake: Record<string, MakeModelOption[]>,
  search: string
): { makes: MakeModelOption[]; modelsByMake: Record<string, MakeModelOption[]> } => {
  const query = search.trim().toLowerCase();
  if (!query) {
    return { makes, modelsByMake };
  }

  const nextModelsByMake: Record<string, MakeModelOption[]> = {};
  const matchingMakes: MakeModelOption[] = [];

  makes.forEach((make) => {
    const makeText = (typeof make.label === 'string' ? make.label : make.value || '').toLowerCase();
    const makeMatches = makeText.includes(query);
    const filteredModels = (modelsByMake[make.value] ?? []).filter((model) => {
      const modelText = (
        typeof model.label === 'string' ? model.label : model.value || ''
      ).toLowerCase();
      return modelText.includes(query);
    });

    if (makeMatches || filteredModels.length) {
      matchingMakes.push(make);
      nextModelsByMake[make.value] = makeMatches
        ? (modelsByMake[make.value] ?? [])
        : filteredModels;
    }
  });

  return { makes: matchingMakes, modelsByMake: nextModelsByMake };
};

/** Models by Make from org combination options ∩ configuredModels. */
export const buildModelsByMakeFromCombinations = (
  makes: MakeModelOption[],
  independentCombinationOptions:
    | {
        independentFieldValue?: string | null;
        dependentFieldOptions?: (string | null)[] | null;
      }[]
    | null
    | undefined,
  configuredModels?: (string | null)[] | null,
  includeNoneOption?: MakeModelOption
): Record<string, MakeModelOption[]> => {
  const configuredSet = new Set((configuredModels ?? []).filter((v): v is string => v != null));
  const result: Record<string, MakeModelOption[]> = {};

  makes.forEach((make) => {
    const modelValues = new Set<string>();

    independentCombinationOptions
      ?.filter((c) => c.independentFieldValue === make.value)
      .forEach((combo) => {
        (combo.dependentFieldOptions ?? []).forEach((v) => {
          if (!v || !configuredSet.has(v)) {
            return;
          }
          modelValues.add(v);
        });
      });

    const models = Array.from(modelValues).map((value) => ({ value, label: value }));
    result[make.value] = includeNoneOption ? [includeNoneOption, ...models] : models;
  });

  return result;
};

export const assetMatchesAssetTypes = (
  asset: { assetType?: { id?: string | null; name?: string | null } | null },
  types: { id?: string; name?: string }[]
): boolean => {
  if (!types.length) {
    return true;
  }
  return types.some(
    (type) =>
      (!!type.id && type.id === asset.assetType?.id) ||
      (!!type.name && type.name === asset.assetType?.name)
  );
};

type AssetWithMakeModelFields = {
  fieldValues?:
    | ({
        fieldDefinition: { name: string };
        selectValue?: string | null;
      } | null)[]
    | null;
};

export const buildMakeOptionsFromAssets = <T extends AssetWithMakeModelFields>(
  assets: T[],
  includeNoneOption?: MakeModelOption
): MakeModelOption[] => {
  if (!assets.length) {
    return [];
  }

  const makes = uniqBy(
    assets
      .map((asset) => {
        const makeField = asset.fieldValues?.find((v) => v?.fieldDefinition.name === 'Make');
        return makeField?.selectValue ? { value: makeField.selectValue } : undefined;
      })
      .filter((v): v is { value: string } => !!v),
    'value'
  );

  return includeNoneOption ? [includeNoneOption, ...makes] : makes;
};

/** Models by Make from Asset fieldValues. */
export const buildModelsByMakeFromAssets = <
  T extends {
    fieldValues?:
      | ({
          fieldDefinition: { name: string };
          selectValue?: string | null;
        } | null)[]
      | null;
    assetType?: { id?: string | null; name?: string | null } | null;
  },
>({
  assets,
  assetTypeIds,
  assetTypeNames,
  makes,
  includeNoneOption,
}: {
  assets: T[];
  assetTypeIds: string[];
  assetTypeNames?: string[];
  makes: MakeModelOption[];
  includeNoneOption?: MakeModelOption;
}): Record<string, MakeModelOption[]> => {
  const modelsByMake = new Map<string, Set<string>>();

  makes.forEach((make) => {
    modelsByMake.set(make.value, new Set());
  });

  assets
    .filter((a) =>
      assetMatchesAssetTypes(
        a,
        [
          ...assetTypeIds.map((id) => ({ id })),
          ...(assetTypeNames ?? []).map((name) => ({ name })),
        ].filter((type) => type.id || type.name)
      )
    )
    .forEach((a) => {
      const makeField = a.fieldValues?.find((v) => v?.fieldDefinition.name === 'Make');
      const modelField = a.fieldValues?.find((v) => v?.fieldDefinition.name === 'Model');
      const makeValue = makeField?.selectValue ?? '';
      const modelValue = modelField?.selectValue;

      if (!modelsByMake.has(makeValue)) {
        modelsByMake.set(makeValue, new Set());
      }

      if (modelValue) {
        modelsByMake.get(makeValue)!.add(modelValue);
      } else if (includeNoneOption) {
        modelsByMake.get(makeValue)!.add(includeNoneOption.value);
      }
    });

  const result: Record<string, MakeModelOption[]> = {};
  makes.forEach((make) => {
    const modelValues = Array.from(modelsByMake.get(make.value) ?? []);
    const models = modelValues
      .filter((v) => v !== includeNoneOption?.value)
      .map((value) => ({ value, label: value }));
    result[make.value] = includeNoneOption ? [includeNoneOption, ...models] : models;
  });

  return result;
};
